import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ScanTicketZ, { champsLus } from "../components/ScanTicketZ";
import { getEvents, getFoodTrucks, createEvent, getKPIs, proposeEvent, validateEvent, refuseEvent, getPendingEvents, bulkDeleteEvents, submitBilanPrestation, getBilanPrestation } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { BulkSelectBar, useSelection } from "../components/ui/bulk-select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  MapPin,
  Users,
  Clock,
  Eye,
  Award,
  TrendingUp,
  Target,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  Send,
  Trash2,
  FileText,
  Euro,
  UtensilsCrossed,
  Bell,
  ThumbsUp,
  ThumbsDown,
  ChefHat,
} from "lucide-react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  parseISO,
  isToday,
  getDay,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import { fr } from "date-fns/locale";
import ParticipationRequestsSection from "../components/ParticipationRequestsSection";
import ProposeEventDialog from "../components/ProposeEventDialog";

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [foodTrucks, setFoodTrucks] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isProposeDialogOpen, setIsProposeDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validatingId, setValidatingId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const { isAdmin, user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Helper function to check if user can manage events (admin or bureau)
  const canManageEvents = () => isAdmin() || user?.role === "bureau";
  
  // Check if user is admin or bureau (for filtering)
  const isAdminOrBureau = isAdmin() || user?.role === "bureau";

  // Filter events based on user role
  // Members only see: events they created OR events they participate in
  const visibleEvents = useMemo(() => {
    if (isAdminOrBureau) {
      // Admin/Bureau see all events
      return events;
    }
    
    // Standard members see only their events
    const ftId = user?.food_truck_id;
    if (!ftId) return [];
    
    return events.filter(e => {
      // Event created by this user
      if (e.cree_par_food_truck_id === ftId) return true;
      // Event where user is participant
      if (e.participants?.some(p => p.food_truck_id === ftId)) return true;
      // Event where user is apporteur deal
      if (e.apporteur_deal_id === ftId) return true;
      return false;
    });
  }, [events, user?.food_truck_id, isAdminOrBureau]);
  
  // Selection for bulk operations (admin only)
  const validatedEvents = useMemo(() => visibleEvents.filter(e => e.statut === "valide"), [visibleEvents]);
  const selection = useSelection(validatedEvents);
  
  const [formData, setFormData] = useState({
    titre: "",
    date_debut: "",
    date_fin: "",
    lieu: "",
    adresse: "",
    information_complementaire: "",
    menu: "",
    type_evenement: "collectif",
    participants: [],
    apporteur_deal_id: "",
    is_sondage: false,
    nombre_food_trucks_requis: 1,
  });
  const [proposeData, setProposeData] = useState({
    titre: "",
    date_debut: "",
    date_fin: "",
    lieu: "",
    adresse: "",
    information_complementaire: "",
    type_evenement: "collectif",
    nombre_food_trucks_requis: 1,
    specialites_recherchees: ["Toutes"],
    specialite_recherchee_autre: "",
    proposeur_participe: true,
    responsable_planning_id: "",
  });
  
  // Liste des spécialités disponibles
  const SPECIALITES_OPTIONS = [
    "Toutes",
    "Burger truck",
    "Pizza",
    "Thai food",
    "Japonais",
    "Poké bowl",
    "Tacos mexicains",
    "BBQ / Smokehouse",
    "Crêpes",
    "Vegan food truck",
    "Coffee truck",
    "Bar à cocktails",
    "Péruvien",
    "Amérique du Sud",
    "Autre",
  ];
  
  // Bilan state
  const [showBilanDialog, setShowBilanDialog] = useState(false);
  const [bilanEvent, setBilanEvent] = useState(null);
  const [bilanData, setBilanData] = useState({
    nombre_plats: "",
    ca_ht: "",
    ca_ttc: "",
    commentaire: "",
  });
  const [submittingBilan, setSubmittingBilan] = useState(false);
  const [existingBilan, setExistingBilan] = useState(null);
  
  // Participation requests state
  const [participationRequests, setParticipationRequests] = useState([]);
  const [organisedEventRequests, setOrganisedEventRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  
  // Direct event view state (from email link)
  const [showOrganisedEventDialog, setShowOrganisedEventDialog] = useState(false);
  const [selectedOrganisedEvent, setSelectedOrganisedEvent] = useState(null);
  const [respondingToEvent, setRespondingToEvent] = useState(false);

  // Events pending initial validation (proposals not yet approved) - Admin/Bureau only
  const pendingProposalEvents = isAdminOrBureau ? events.filter(e => e.statut === "en_attente") : [];
  
  // Events waiting for participant selection (already validated, waiting for final selection) - Admin/Bureau only
  const pendingParticipantsEvents = isAdminOrBureau ? events.filter(e => e.statut === "en_attente_participation") : [];
  
  // Events waiting for placement validation by Bureau - Admin/Bureau only
  const pendingPlacementEvents = isAdminOrBureau ? events.filter(e => e.statut === "en_attente_placement") : [];
  
  // Total pending for badge
  const totalPendingCount = pendingProposalEvents.length + pendingParticipantsEvents.length + pendingPlacementEvents.length;

  // Handle event parameter from URL (from email link)
  useEffect(() => {
    const eventId = searchParams.get('event');
    if (eventId && events.length > 0) {
      const event = events.find(e => e.id === eventId);
      if (event) {
        // Navigate to event detail page
        navigate(`/events/${eventId}`);
      }
    }
  }, [searchParams, events, navigate]);

  // Handle organised_event_id from email link
  useEffect(() => {
    const organisedEventId = searchParams.get('organised_event_id');
    if (organisedEventId) {
      loadOrganisedEventDetails(organisedEventId);
    }
  }, [searchParams]);

  const loadOrganisedEventDetails = async (eventId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organised-events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const eventData = await response.json();
        setSelectedOrganisedEvent(eventData);
        setShowOrganisedEventDialog(true);
      } else {
        toast.error("Événement non trouvé");
      }
    } catch (error) {
      console.error("Error loading organised event:", error);
      toast.error("Erreur de chargement de l'événement");
    }
  };

  const respondToOrganisedEvent = async (interested) => {
    if (!selectedOrganisedEvent) return;
    setRespondingToEvent(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organised-events/${selectedOrganisedEvent.id}/respond?interested=${interested}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        toast.success(interested ? "Merci ! Votre intérêt a été enregistré" : "Réponse enregistrée");
        setShowOrganisedEventDialog(false);
        setSelectedOrganisedEvent(null);
        // Clear URL parameter
        navigate('/calendar', { replace: true });
        // Reload requests
        loadParticipationRequests();
      } else {
        const data = await response.json();
        // More specific error messages
        if (data.detail?.includes("non trouvee") || data.detail?.includes("not found")) {
          toast.error("Vous n'avez pas été invité à cet événement");
        } else if (data.detail?.includes("food truck")) {
          toast.error("Vous devez être associé à un food truck pour participer");
        } else {
          toast.error(data.detail || "Erreur lors de l'enregistrement");
        }
      }
    } catch (error) {
      console.error("Error responding to event:", error);
      toast.error("Erreur de connexion au serveur");
    } finally {
      setRespondingToEvent(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [eventsRes, ftRes, kpisRes] = await Promise.all([getEvents(), getFoodTrucks(), getKPIs()]);
      setEvents(eventsRes.data);
      setFoodTrucks(ftRes.data);
      setKpis(kpisRes.data);
      
      // Load participation requests for non-admin users
      if (user?.food_truck_id) {
        loadParticipationRequests();
      }
    } catch (error) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  
  const loadParticipationRequests = async () => {
    setLoadingRequests(true);
    try {
      const token = localStorage.getItem("token");
      
      // Load normal event participation requests
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/my-participation-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setParticipationRequests(data || []);
      
      // Load organised event participation requests
      const orgResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organised-events/my-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (orgResponse.ok) {
        const orgData = await orgResponse.json();
        setOrganisedEventRequests(orgData || []);
      }
    } catch (error) {
      console.error("Error loading participation requests:", error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleBulkDeleteEvents = async () => {
    const ids = selection.getSelectedIds();
    if (ids.length === 0) return;
    
    setIsDeleting(true);
    try {
      const res = await bulkDeleteEvents(ids);
      toast.success(res.data.message);
      selection.deselectAll();
      loadData();
    } catch (error) {
      toast.error("Erreur de suppression en lot");
    } finally {
      setIsDeleting(false);
    }
  };

  const openBilanDialog = async (event) => {
    setBilanEvent(event);
    setBilanData({ nombre_plats: "", ca_ht: "", ca_ttc: "", commentaire: "" });
    setExistingBilan(null);
    
    // Check if bilan already exists
    const ftId = user?.food_truck_id;
    if (ftId) {
      try {
        const res = await getBilanPrestation(event.id, ftId);
        if (res.data.rempli) {
          setExistingBilan(res.data);
          setBilanData({
            nombre_plats: res.data.nombre_plats || "",
            ca_ht: res.data.ca_ht || "",
            ca_ttc: res.data.ca_ttc || "",
            commentaire: res.data.commentaire || "",
          });
        }
      } catch (error) {
        // Bilan doesn't exist yet, that's fine
      }
    }
    
    setShowBilanDialog(true);
  };

  const handleSubmitBilan = async () => {
    if (!bilanEvent || !user?.food_truck_id) return;
    
    if (!bilanData.nombre_plats || !bilanData.ca_ht || !bilanData.ca_ttc) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    setSubmittingBilan(true);
    try {
      await submitBilanPrestation(bilanEvent.id, user.food_truck_id, {
        nombre_plats: parseInt(bilanData.nombre_plats),
        ca_ht: parseFloat(String(bilanData.ca_ht).replace(/\s/g, "").replace(",", ".")),
        ca_ttc: parseFloat(String(bilanData.ca_ttc).replace(/\s/g, "").replace(",", ".")),
        commentaire: bilanData.commentaire,
      });
      toast.success("Bilan de prestation enregistré");
      setShowBilanDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSubmittingBilan(false);
    }
  };

  // Check if event is past (ended)
  const isEventPast = (event) => {
    const endDate = new Date(event.date_fin || event.date_debut);
    return endDate < new Date();
  };

  // Check if current user participated in event
  const didParticipate = (event) => {
    if (!user?.food_truck_id) return false;
    return event.participants?.some(p => p.food_truck_id === user.food_truck_id);
  };

  const handleProposeEvent = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await proposeEvent(proposeData);
      toast.success("Événement proposé ! En attente de validation par l'admin.");
      setIsProposeDialogOpen(false);
      setProposeData({
        titre: "",
        date_debut: "",
        date_fin: "",
        lieu: "",
        adresse: "",
        information_complementaire: "",
        type_evenement: "collectif",
        nombre_food_trucks_requis: 1,
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la proposition");
    } finally {
      setSubmitting(false);
    }
  };

  const handleValidateEvent = async (eventId) => {
    setValidatingId(eventId);
    try {
      await validateEvent(eventId);
      toast.success("Événement validé !");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la validation");
    } finally {
      setValidatingId(null);
    }
  };

  const handleRefuseEvent = async (eventId) => {
    const reason = prompt("Raison du refus (optionnel) :");
    setValidatingId(eventId);
    try {
      await refuseEvent(eventId, reason);
      toast.success("Événement refusé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors du refus");
    } finally {
      setValidatingId(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        apporteur_deal_id: formData.apporteur_deal_id || null,
      };
      await createEvent(submitData);
      toast.success("Événement créé avec succès");
      setIsDialogOpen(false);
      setFormData({
        titre: "",
        date_debut: "",
        date_fin: "",
        lieu: "",
        adresse: "",
        information_complementaire: "",
        menu: "",
        type_evenement: "collectif",
        participants: [],
        apporteur_deal_id: "",
        is_sondage: false,
        nombre_food_trucks_requis: 1,
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de création");
    }
  };

  // Calendar logic
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const getEventsForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return visibleEvents.filter((e) => {
      // Extract only the date part (YYYY-MM-DD) to handle datetime formats
      const eventStart = e.date_debut?.split('T')[0] || e.date_debut;
      const eventEnd = e.date_fin?.split('T')[0] || e.date_fin;
      return eventStart <= dateStr && eventEnd >= dateStr;
    });
  };

  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  const weekDays = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

  const eventColors = {
    collectif: "bg-primary text-white",
    individuel: "bg-accent text-black",
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-black text-primary">
            Planning
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Calendrier des événements
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Propose event button - ONLY for regular members with food truck (NOT admin/bureau) */}
          {user?.food_truck_id && !canManageEvents() && (
            <ProposeEventDialog
              isOpen={isProposeDialogOpen}
              onOpenChange={setIsProposeDialogOpen}
              token={token}
              onEventProposed={loadData}
            />
          )}

          
          {/* Admin/Bureau: Create event button (direct creation) */}
          {canManageEvents() && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-primary hover:bg-primary-hover text-white shadow-md text-sm"
                  size="sm"
                  data-testid="add-event-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Nouvel</span> événement
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-auto">
                <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-primary">
                  Créer un événement
                </DialogTitle>
                <DialogDescription>
                  {formData.type_evenement === "collectif" && (
                    <span className="text-status-info font-medium">
                      +20€ seront automatiquement ajoutés pour chaque participant
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label className="text-secondary font-semibold">Titre *</Label>
                  <Input
                    value={formData.titre}
                    onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                    placeholder="Nom de l'événement"
                    data-testid="input-titre"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-secondary font-semibold">Date de début *</Label>
                    <Input
                      type="date"
                      value={formData.date_debut}
                      onChange={(e) => setFormData({ ...formData, date_debut: e.target.value })}
                      className="mt-1 border-[#E5E0DC]"
                      data-testid="input-date-debut"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Date de fin *</Label>
                    <Input
                      type="date"
                      value={formData.date_fin}
                      onChange={(e) => setFormData({ ...formData, date_fin: e.target.value })}
                      className="mt-1 border-[#E5E0DC]"
                      data-testid="input-date-fin"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-secondary font-semibold">Lieu *</Label>
                    <Input
                      value={formData.lieu}
                      onChange={(e) => setFormData({ ...formData, lieu: e.target.value })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="Nom du lieu"
                      data-testid="input-lieu"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Type *</Label>
                    <Select
                      value={formData.type_evenement}
                      onValueChange={(val) => setFormData({ ...formData, type_evenement: val })}
                    >
                      <SelectTrigger className="mt-1 border-[#E5E0DC]" data-testid="select-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="collectif">Collectif (+20€/participant)</SelectItem>
                        <SelectItem value="individuel">Individuel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-secondary font-semibold">Adresse</Label>
                  <Input
                    value={formData.adresse}
                    onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                    placeholder="Adresse complète"
                  />
                </div>
                <div>
                  <Label className="text-secondary font-semibold">Informations complémentaires</Label>
                  <Textarea
                    value={formData.information_complementaire}
                    onChange={(e) => setFormData({ ...formData, information_complementaire: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                    placeholder="Détails supplémentaires..."
                    rows={3}
                  />
                </div>
                <div>
                  <Label className="text-secondary font-semibold">Menu</Label>
                  <Textarea
                    value={formData.menu}
                    onChange={(e) => setFormData({ ...formData, menu: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                    placeholder="Menu proposé..."
                    rows={2}
                  />
                </div>
                <div>
                  <Label className="text-secondary font-semibold">Apporteur du deal</Label>
                  <Select
                    value={formData.apporteur_deal_id || "none"}
                    onValueChange={(val) => setFormData({ ...formData, apporteur_deal_id: val === "none" ? "" : val })}
                  >
                    <SelectTrigger className="mt-1 border-[#E5E0DC]" data-testid="select-apporteur">
                      <SelectValue placeholder="Qui a apporté ce deal ?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {foodTrucks.filter(ft => ft.statut === "actif").map((ft) => (
                        <SelectItem key={ft.id} value={ft.id}>
                          {ft.nom_food_truck}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-secondary font-semibold">Nb de food trucks demandés</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={formData.nombre_food_trucks_requis === 1 ? "" : formData.nombre_food_trucks_requis}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({ ...formData, nombre_food_trucks_requis: val === "" ? 1 : parseInt(val) || 1 });
                      }}
                      className="mt-1 border-[#E5E0DC]"
                      data-testid="input-nombre-food-trucks"
                      placeholder="1"
                    />
                  </div>
                  <div className="flex items-end">
                    <div className="flex items-center gap-2 pb-2">
                      <Checkbox
                        id="is_sondage"
                        checked={formData.is_sondage}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_sondage: checked })}
                      />
                      <label htmlFor="is_sondage" className="text-sm cursor-pointer text-secondary font-medium">
                        Créer comme sondage (les FT pourront se positionner)
                      </label>
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-secondary font-semibold mb-3 block">Participants</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-3 bg-[#F9F7F5] rounded-lg">
                    {foodTrucks.filter(ft => ft.statut === "actif").map((ft) => (
                      <div key={ft.id} className="flex items-center gap-2">
                        <Checkbox
                          id={ft.id}
                          checked={formData.participants.includes(ft.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({ ...formData, participants: [...formData.participants, ft.id] });
                            } else {
                              setFormData({ ...formData, participants: formData.participants.filter((id) => id !== ft.id) });
                            }
                          }}
                        />
                        <label htmlFor={ft.id} className="text-sm cursor-pointer">
                          {ft.nom_food_truck}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="submit-event-btn">
                    Créer l'événement
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {/* KPIs */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <CalendarIcon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    {canManageEvents() ? "Total événements" : "Mes événements"}
                  </p>
                  <p className="text-xl font-heading font-bold text-secondary">
                    {canManageEvents() ? kpis.total_evenements : visibleEvents.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-accent/10">
                  <Users className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Collectifs</p>
                  <p className="text-xl font-heading font-bold text-accent">
                    {canManageEvents() 
                      ? kpis.evenements_collectifs 
                      : visibleEvents.filter(e => e.type_evenement === "collectif").length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <Target className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Individuels</p>
                  <p className="text-xl font-heading font-bold text-secondary">
                    {canManageEvents() 
                      ? kpis.evenements_individuels 
                      : visibleEvents.filter(e => e.type_evenement === "individuel").length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-status-success/10">
                  <TrendingUp className="w-5 h-5 text-status-success" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Ce mois</p>
                  <p className="text-xl font-heading font-bold text-status-success">
                    {canManageEvents() 
                      ? kpis.evenements_ce_mois 
                      : visibleEvents.filter(e => {
                          const now = new Date();
                          const eventDate = new Date(e.date_debut);
                          return eventDate.getMonth() === now.getMonth() && eventDate.getFullYear() === now.getFullYear();
                        }).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Participation Requests - For Food Trucks */}
      {/* Members see their participation requests */}
      {/* Bureau members also see their own requests if they have a food truck */}
      {user?.food_truck_id && (participationRequests.length > 0 || organisedEventRequests.length > 0) && (
        <ParticipationRequestsSection 
          requests={participationRequests}
          organisedRequests={organisedEventRequests}
          token={token}
          onRequestHandled={loadData}
        />
      )}

      {/* Pending Proposals for Admin/Bureau - Events waiting for initial validation */}
      {canManageEvents() && pendingProposalEvents.length > 0 && (
        <Card className="bg-status-warning/5 border-status-warning">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-status-warning flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Propositions en attente ({pendingProposalEvents.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingProposalEvents.map((event) => (
                <div key={event.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-[#E5E0DC]">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-secondary">{event.titre}</h4>
                      <Badge variant="outline" className="border-status-warning text-status-warning text-xs">
                        En attente
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {event.lieu} • {format(parseISO(event.date_debut), "dd/MM/yyyy", { locale: fr })}
                    </p>
                    {event.cree_par_nom && (
                      <p className="text-xs text-muted-foreground">
                        Proposé par : <span className="font-medium">{event.cree_par_nom}</span>
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-status-success text-status-success hover:bg-status-success/10"
                      onClick={() => handleValidateEvent(event.id)}
                      disabled={validatingId === event.id}
                      data-testid={`validate-event-${event.id}`}
                    >
                      {validatingId === event.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Valider
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-status-error text-status-error hover:bg-status-error/10"
                      onClick={() => handleRefuseEvent(event.id)}
                      disabled={validatingId === event.id}
                      data-testid={`refuse-event-${event.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Refuser
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`/events/${event.id}`)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Participant Selection - Events already validated but waiting for participant selection */}
      {canManageEvents() && pendingParticipantsEvents.length > 0 && (
        <Card className="bg-blue-50/50 border-blue-300">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-blue-700 flex items-center gap-2">
              <Users className="w-5 h-5" />
              En attente de sélection ({pendingParticipantsEvents.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-blue-600 mb-3">
              Ces événements ont été validés. Sélectionnez les participants finaux.
            </p>
            <div className="space-y-3">
              {pendingParticipantsEvents.map((event) => (
                <div key={event.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-secondary">{event.titre}</h4>
                      <Badge variant="outline" className="border-blue-500 text-blue-600 text-xs">
                        Sélection en cours
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {event.lieu} • {format(parseISO(event.date_debut), "dd/MM/yyyy", { locale: fr })}
                    </p>
                    {event.cree_par_nom && (
                      <p className="text-xs text-muted-foreground">
                        Proposé par : <span className="font-medium">{event.cree_par_nom}</span>
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="default"
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => navigate(`/events/${event.id}`)}
                      data-testid={`select-participants-${event.id}`}
                    >
                      <Users className="w-4 h-4 mr-1" />
                      Sélectionner
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Placement Validation - Events with participants selected, waiting for Bureau to validate placement */}
      {canManageEvents() && pendingPlacementEvents.length > 0 && (
        <Card className="bg-purple-50/50 border-purple-300">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-purple-700 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              En attente de validation du placement ({pendingPlacementEvents.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-purple-600 mb-3">
              Les participants ont été sélectionnés. Validez le placement pour envoyer les confirmations.
            </p>
            <div className="space-y-3">
              {pendingPlacementEvents.map((event) => (
                <div key={event.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-200">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-secondary">{event.titre}</h4>
                      <Badge variant="outline" className="border-purple-500 text-purple-600 text-xs">
                        Placement à valider
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {event.lieu} • {format(parseISO(event.date_debut), "dd/MM/yyyy", { locale: fr })}
                    </p>
                    <p className="text-xs text-purple-600 mt-1">
                      {event.participants?.length || 0} participant(s) sélectionné(s)
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="default"
                      className="bg-purple-600 hover:bg-purple-700"
                      onClick={() => navigate(`/events/${event.id}`)}
                      data-testid={`validate-placement-${event.id}`}
                    >
                      <MapPin className="w-4 h-4 mr-1" />
                      Valider
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2 bg-white border-[#E5E0DC]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-heading text-xl text-secondary">
              {format(currentDate, "MMMM yyyy", { locale: fr })}
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentDate(subMonths(currentDate, 1))}
                className="border-[#E5E0DC]"
                data-testid="prev-month-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentDate(addMonths(currentDate, 1))}
                className="border-[#E5E0DC]"
                data-testid="next-month-btn"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Week days header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map((day) => (
                <div key={day} className="text-center text-sm font-semibold text-muted-foreground py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, i) => {
                const dayEvents = getEventsForDate(day);
                const isCurrentMonth = isSameMonth(day, currentDate);
                const isSelected = selectedDate && isSameDay(day, selectedDate);
                const today = isToday(day);

                return (
                  <div
                    key={i}
                    onClick={() => setSelectedDate(day)}
                    className={`min-h-[80px] md:min-h-[100px] p-1 md:p-2 rounded-lg cursor-pointer transition-all ${
                      isCurrentMonth ? "bg-[#F9F7F5]" : "bg-[#F9F7F5]/50"
                    } ${isSelected ? "ring-2 ring-primary" : ""} ${
                      today ? "border-2 border-accent" : ""
                    } hover:bg-[#F0EBE6]`}
                    data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
                  >
                    <div className={`text-sm font-semibold mb-1 ${
                      !isCurrentMonth ? "text-muted-foreground/50" : today ? "text-accent" : "text-secondary"
                    }`}>
                      {format(day, "d")}
                    </div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 2).map((event) => (
                        <div
                          key={event.id}
                          className={`text-xs px-1 py-0.5 rounded truncate ${eventColors[event.type_evenement]}`}
                          title={event.titre}
                        >
                          {event.titre}
                        </div>
                      ))}
                      {dayEvents.length > 2 && (
                        <div className="text-xs text-muted-foreground font-medium">
                          +{dayEvents.length - 2} autres
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex gap-4 mt-4 pt-4 border-t border-[#E5E0DC]">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-primary"></div>
                <span className="text-sm text-muted-foreground">Collectif</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-accent"></div>
                <span className="text-sm text-muted-foreground">Individuel</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Selected Date Events */}
        <Card className="bg-white border-[#E5E0DC]">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-primary" />
              {selectedDate
                ? format(selectedDate, "dd MMMM yyyy", { locale: fr })
                : "Sélectionnez une date"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedDate ? (
              selectedDateEvents.length > 0 ? (
                <div className="space-y-4">
                  {selectedDateEvents.map((event) => (
                    <div
                      key={event.id}
                      className={`p-4 rounded-lg hover:bg-[#F0EBE6] transition-colors cursor-pointer ${
                        event.statut === "en_attente" ? "bg-status-warning/10 border border-status-warning/30" :
                        event.statut === "en_attente_participation" ? "bg-blue-50 border border-blue-200" : "bg-[#F9F7F5]"
                      }`}
                      onClick={() => navigate(`/events/${event.id}`)}
                      data-testid={`event-card-${event.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-secondary">{event.titre}</h3>
                        <div className="flex gap-1">
                          {event.statut === "en_attente" && (
                            <Badge variant="outline" className="border-status-warning text-status-warning text-xs">
                              En attente validation
                            </Badge>
                          )}
                          {event.statut === "en_attente_participation" && (
                            <Badge variant="outline" className="border-blue-500 text-blue-600 text-xs">
                              En attente participants
                            </Badge>
                          )}
                          {event.statut === "en_attente_placement" && (
                            <Badge variant="outline" className="border-purple-500 text-purple-600 text-xs">
                              En attente placement
                            </Badge>
                          )}
                          <Badge variant={event.type_evenement === "collectif" ? "default" : "secondary"}>
                            {event.type_evenement}
                          </Badge>
                        </div>
                      </div>
                      {event.cree_par_nom && (event.statut === "en_attente" || event.statut === "en_attente_participation" || event.statut === "en_attente_placement") && (
                        <p className="text-xs text-muted-foreground mb-2">
                          Proposé par : <span className="font-medium">{event.cree_par_nom}</span>
                        </p>
                      )}
                      <div className="space-y-1 text-sm text-muted-foreground">
                        <p className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" /> {event.lieu}
                        </p>
                        <p className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {event.date_debut === event.date_fin 
                            ? event.date_debut 
                            : `${event.date_debut} - ${event.date_fin}`}
                        </p>
                        <p className="flex items-center gap-2">
                          <Users className="w-4 h-4" /> {event.participants?.length || 0} participant(s)
                        </p>
                        {event.apporteur_deal_nom && (
                          <p className="flex items-center gap-2 text-accent font-medium">
                            <Award className="w-4 h-4" /> Deal: {event.apporteur_deal_nom}
                          </p>
                        )}
                        {/* Spécialités recherchées */}
                        {event.specialites_recherchees?.length > 0 && !event.specialites_recherchees.includes("Toutes") && (
                          <p className="flex items-center gap-2 text-orange-600">
                            <ChefHat className="w-4 h-4" /> 
                            Recherche: {event.specialites_recherchees.filter(s => s !== "Autre").join(", ")}
                          </p>
                        )}
                        {/* Statut des demandes de participation */}
                        {event.statut === "en_attente_participation" && (
                          <p className="flex items-center gap-2 text-blue-600 font-medium">
                            <Send className="w-4 h-4" /> Demandes envoyées - en attente des réponses
                          </p>
                        )}
                        {event.statut === "en_attente_placement" && (
                          <p className="flex items-center gap-2 text-purple-600 font-medium">
                            <MapPin className="w-4 h-4" /> Participants sélectionnés - placement à valider
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2 text-primary hover:text-primary-hover"
                      >
                        <Eye className="w-4 h-4 mr-1" /> Voir détails
                      </Button>
                      {/* Bouton Bilan pour les événements passés */}
                      {isEventPast(event) && didParticipate(event) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 ml-2 border-accent text-accent hover:bg-accent/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            openBilanDialog(event);
                          }}
                          data-testid={`bilan-btn-${event.id}`}
                        >
                          <FileText className="w-4 h-4 mr-1" /> Bilan
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  Aucun événement ce jour
                </p>
              )
            ) : (
              <p className="text-center text-muted-foreground py-8">
                Cliquez sur une date pour voir les événements
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog Bilan de Prestation */}
      <Dialog open={showBilanDialog} onOpenChange={setShowBilanDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Bilan de prestation
            </DialogTitle>
            <DialogDescription>
              {bilanEvent?.titre} - {bilanEvent?.date_debut}
            </DialogDescription>
          </DialogHeader>
          
          {existingBilan && (
            <div className="p-3 bg-status-success/10 rounded-lg border border-status-success/30 mb-4">
              <p className="text-sm text-status-success flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Bilan déjà rempli le {new Date(existingBilan.date).toLocaleDateString('fr-FR')}
              </p>
            </div>
          )}
          
          <div className="space-y-4">
            <ScanTicketZ onLu={(r) => setBilanData((b) => ({ ...b, ...champsLus(r) }))} />
            <div>
              <Label className="text-secondary font-semibold flex items-center gap-2">
                <UtensilsCrossed className="w-4 h-4 text-primary" />
                Nombre de plats vendus *
              </Label>
              <Input
                type="text"
                inputMode="numeric"
                value={bilanData.nombre_plats}
                onChange={(e) => setBilanData({ ...bilanData, nombre_plats: e.target.value })}
                className="mt-1 border-[#E5E0DC]"
                placeholder="Ex: 150"
                data-testid="bilan-nombre-plats"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-secondary font-semibold flex items-center gap-2">
                  <Euro className="w-4 h-4 text-primary" />
                  CA HT (€) *
                </Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={bilanData.ca_ht}
                  onChange={(e) => setBilanData({ ...bilanData, ca_ht: e.target.value })}
                  className="mt-1 border-[#E5E0DC]"
                  placeholder="Ex: 1200.00"
                  data-testid="bilan-ca-ht"
                />
              </div>
              <div>
                <Label className="text-secondary font-semibold flex items-center gap-2">
                  <Euro className="w-4 h-4 text-accent" />
                  CA TTC (€) *
                </Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={bilanData.ca_ttc}
                  onChange={(e) => setBilanData({ ...bilanData, ca_ttc: e.target.value })}
                  className="mt-1 border-[#E5E0DC]"
                  placeholder="Ex: 1440.00"
                  data-testid="bilan-ca-ttc"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-secondary font-semibold">Commentaire (optionnel)</Label>
              <Textarea
                value={bilanData.commentaire}
                onChange={(e) => setBilanData({ ...bilanData, commentaire: e.target.value })}
                className="mt-1 border-[#E5E0DC]"
                placeholder="Remarques sur la prestation..."
                rows={3}
                data-testid="bilan-commentaire"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBilanDialog(false)}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSubmitBilan}
              disabled={submittingBilan}
              className="bg-primary hover:bg-primary-hover"
              data-testid="submit-bilan-btn"
            >
              {submittingBilan ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              {existingBilan ? "Modifier" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Organised Event Details Dialog (from email link) */}
      <Dialog open={showOrganisedEventDialog} onOpenChange={setShowOrganisedEventDialog}>
        <DialogContent className="max-w-lg" data-testid="organised-event-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl text-primary flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" />
              {selectedOrganisedEvent?.titre || "Événement"}
            </DialogTitle>
            <DialogDescription>
              Êtes-vous intéressé(e) par cet événement ?
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrganisedEvent && (
            <div className="space-y-4">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <div className="space-y-2 text-sm">
                  <p className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-amber-600" />
                    <span className="font-medium">Lieu:</span> {selectedOrganisedEvent.lieu || "Non précisé"}
                  </p>
                  {selectedOrganisedEvent.adresse && (
                    <p className="flex items-center gap-2 ml-6 text-muted-foreground">
                      {selectedOrganisedEvent.adresse}
                    </p>
                  )}
                  <p className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-amber-600" />
                    <span className="font-medium">Dates:</span> 
                    {selectedOrganisedEvent.dates?.map(d => d.date).join(", ") || "Non précisé"}
                  </p>
                  <p className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-amber-600" />
                    <span className="font-medium">Food trucks recherchés:</span> {selectedOrganisedEvent.nombre_food_trucks_requis || "?"}
                  </p>
                  {selectedOrganisedEvent.specialites_recherchees?.length > 0 && (
                    <div className="flex items-start gap-2">
                      <ChefHat className="w-4 h-4 text-amber-600 mt-0.5" />
                      <div>
                        <span className="font-medium">Spécialités:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedOrganisedEvent.specialites_recherchees.map((spec, i) => (
                            <Badge key={i} variant="outline" className="text-xs border-amber-300 text-amber-700">
                              {spec}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedOrganisedEvent.couverts_estimes && (
                    <p className="flex items-center gap-2">
                      <UtensilsCrossed className="w-4 h-4 text-amber-600" />
                      <span className="font-medium">Couverts estimés:</span> {selectedOrganisedEvent.couverts_estimes}
                    </p>
                  )}
                </div>
              </div>
              
              {selectedOrganisedEvent.description && (
                <div className="text-sm text-muted-foreground bg-gray-50 p-3 rounded-lg">
                  <p className="font-medium text-foreground mb-1">Description:</p>
                  {selectedOrganisedEvent.description}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button 
              variant="outline" 
              className="border-red-300 text-red-600 hover:bg-red-50 flex-1"
              onClick={() => respondToOrganisedEvent(false)}
              disabled={respondingToEvent}
              data-testid="btn-not-interested-dialog"
            >
              {respondingToEvent ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <ThumbsDown className="w-4 h-4 mr-2" />
              )}
              Pas intéressé
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700 text-white flex-1"
              onClick={() => respondToOrganisedEvent(true)}
              disabled={respondingToEvent}
              data-testid="btn-interested-dialog"
            >
              {respondingToEvent ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <ThumbsUp className="w-4 h-4 mr-2" />
              )}
              Intéressé
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
